import React, { useState, useEffect, useRef, useCallback } from 'react';
import { track, trackAmazon } from './tracking';
import { LINKS } from './copy';

const Quiz = ({ lang, t }) => {
  // el botón del libro lleva a la edición del idioma de la página
  const edition = lang === 'es' ? 'es' : 'en';
  const bookHref = edition === 'es' ? LINKS.kindleEs : LINKS.kindleEn;
  const [currentStep, setCurrentStep] = useState('intro'); // 'intro' | 0-9 | 'result' | 'detail'
  const [answers, setAnswers] = useState(Array(10).fill(null));
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [fade, setFade] = useState(true);
  const [copied, setCopied] = useState(false);
  const [animatedBars, setAnimatedBars] = useState(false);
  const quizRef = useRef(null);

  const questions = t.quizQuestions;
  const dimensions = t.quizDimensions;
  const categories = t.quizCategories;
  const dimensionDetail = t.quizDimensionDetail;
  const report = t.quizReport;

  const trackQuizEvent = useCallback((eventName, params = {}) => track(eventName, params), []);

  const totalScore = answers.reduce((sum, a) => sum + (a !== null ? a : 0), 0);

  const getDimensionScore = (dimKey) => {
    const dim = dimensions[dimKey];
    return dim.questions.reduce((sum, qi) => sum + (answers[qi] !== null ? answers[qi] : 0), 0);
  };

  const getCategory = () => {
    for (const [, cat] of Object.entries(categories)) {
      if (totalScore >= cat.range[0] && totalScore <= cat.range[1]) return cat;
    }
    return categories.starting_scratch;
  };

  const getWeakestDimension = () => {
    let weakest = null;
    let lowestPct = Infinity;
    for (const [key, dim] of Object.entries(dimensions)) {
      const score = getDimensionScore(key);
      const pct = score / dim.maxPoints;
      if (pct < lowestPct) {
        lowestPct = pct;
        weakest = key;
      }
    }
    return weakest;
  };

  const transition = (next) => {
    setFade(false);
    setTimeout(() => {
      setCurrentStep(next);
      setShowExplanation(false);
      setFade(true);
    }, 300);
  };

  const handleAnswer = (questionIndex, points) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = points;
    setAnswers(newAnswers);
    setShowExplanation(true);
    trackQuizEvent('quiz_question_answered', {
      question: questionIndex + 1,
      points: points
    });
  };

  const handleNext = () => {
    if (typeof currentStep === 'number' && currentStep < 9) {
      transition(currentStep + 1);
    } else if (currentStep === 9) {
      transition('result');
      trackQuizEvent('quiz_completed', {
        score: totalScore,
        category: Object.keys(categories).find(k => {
          const c = categories[k];
          return totalScore >= c.range[0] && totalScore <= c.range[1];
        })
      });
    }
  };

  const handleBack = () => {
    if (typeof currentStep === 'number' && currentStep > 0) {
      transition(currentStep - 1);
    } else if (currentStep === 'result') {
      transition(9);
    }
  };

  const handleStart = () => {
    trackQuizEvent('quiz_started');
    transition(0);
  };

  const handleRetake = () => {
    setAnswers(Array(10).fill(null));
    setEmail('');
    setEmailSubmitted(false);
    setAnimatedBars(false);
    transition('intro');
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      // Subscribe to Substack (fire-and-forget via form-urlencoded simple request)
      const formBody = new URLSearchParams({
        email: email.trim(),
        source: 'quiz',
        first_url: window.location.href,
        first_referrer: document.referrer || '',
        current_url: window.location.href,
        current_referrer: document.referrer || ''
      });
      fetch('https://nachoparietti.substack.com/api/v1/free?nojs=true', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
        mode: 'no-cors'
      }).catch(() => {
        // Silently fail: email is also saved locally as backup
      });

      localStorage.setItem('quiz_email', email);
      localStorage.setItem('quiz_score', totalScore.toString());
      localStorage.setItem('quiz_answers', JSON.stringify(answers));
      setEmailSubmitted(true);
      trackQuizEvent('quiz_email_submitted');
      track('generate_lead', { origin: 'quiz' });
      transition('detail');
    }
  };

  const handleShare = () => {
    const text = t.quizShareText.replace('{score}', totalScore);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackQuizEvent('quiz_shared');
    });
  };

  // Animate bars on result/detail view
  useEffect(() => {
    if (currentStep === 'result' || currentStep === 'detail') {
      const timer = setTimeout(() => setAnimatedBars(true), 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Scroll to top on step change
  useEffect(() => {
    if (currentStep !== 'intro' && quizRef.current) {
      quizRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const renderProgressBar = () => {
    if (typeof currentStep !== 'number') return null;
    const progress = ((currentStep + 1) / 10) * 100;
    return (
      <div className="mb-8">
        <div className="flex justify-between text-sm text-white/60 mb-2">
          <span>{t.quizQuestionLabel} {currentStep + 1} {t.quizProgressOf} 10</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className="bg-white h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  const renderDimensionBars = (showWeakest = false) => {
    const weakest = getWeakestDimension();
    return (
      <div className="space-y-4">
        {Object.entries(dimensions).map(([key, dim], i) => {
          const score = getDimensionScore(key);
          const pct = (score / dim.maxPoints) * 100;
          const isWeakest = key === weakest;
          return (
            <div key={key} className={`${isWeakest && showWeakest ? 'border border-yellow-400/50 rounded-lg p-3' : ''}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">
                  {dim.label}
                  {isWeakest && showWeakest && (
                    <span className="ml-2 text-xs bg-yellow-400/20 text-yellow-400 px-2 py-0.5 rounded">{t.quizWeakestArea}</span>
                  )}
                </span>
                <span className="text-white/60">{score}/{dim.maxPoints}</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all duration-500"
                  style={{
                    width: animatedBars ? `${pct}%` : '0%',
                    transitionDelay: `${i * 100}ms`,
                    backgroundColor: pct >= 70 ? '#4ade80' : pct >= 40 ? '#fbbf24' : '#f87171'
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderIntro = () => (
    <div className="text-center max-w-2xl mx-auto">
      <div className="bg-white/10 w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-8">
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Open Sans' }}>
        {t.quizTitle}
      </h3>
      <p className="text-lg text-white/80 mb-8">
        {t.quizSubtitle}
      </p>
      <button
        onClick={handleStart}
        className="px-8 py-4 bg-white text-black rounded-lg hover:bg-opacity-90 transition font-medium text-lg"
      >
        {t.quizStartButton}
      </button>
      <p className="text-sm text-white/50 mt-4">{t.quizBasedOn}</p>
    </div>
  );

  const renderQuestion = () => {
    const q = questions[currentStep];
    const selectedPoints = answers[currentStep];
    // If feedback for exact points doesn't exist, find closest match
    const getFeedback = () => {
      if (selectedPoints === null) return null;
      if (q.feedback[selectedPoints] !== undefined) return q.feedback[selectedPoints];
      // Find the closest key
      const keys = Object.keys(q.feedback).map(Number);
      const closest = keys.reduce((prev, curr) =>
        Math.abs(curr - selectedPoints) < Math.abs(prev - selectedPoints) ? curr : prev
      );
      return q.feedback[closest];
    };

    return (
      <div className="max-w-3xl mx-auto">
        {renderProgressBar()}

        <div className="mb-2">
          <span className="text-xs font-semibold tracking-wider text-white/50 uppercase">
            {q.dimension}
          </span>
        </div>

        <p data-avoid className="text-sm text-white/60 mb-6 italic">{q.subtitle}</p>

        <h3 data-avoid className="text-xl md:text-2xl font-bold mb-8" style={{ fontFamily: 'Open Sans' }}>
          {q.question}
        </h3>

        <div className="grid gap-3 md:grid-cols-2">
          {q.options.map((opt, i) => {
            const isSel = selectedPoints === opt.points && answers[currentStep] !== null;
            return (
            <button
              key={i}
              data-selected={isSel ? 'true' : undefined}
              data-avoid={isSel ? 'true' : undefined}
              onClick={() => handleAnswer(currentStep, opt.points)}
              className={`text-left p-4 rounded-lg border transition-all duration-300 ${
                selectedPoints === opt.points && answers[currentStep] !== null
                  ? 'border-white bg-white/20'
                  : `border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/30 ${answers[currentStep] !== null ? 'opacity-60 hover:opacity-100' : ''}`
              }`}
            >
              <span className="text-sm md:text-base">{opt.text}</span>
            </button>
            );
          })}
        </div>

        {showExplanation && getFeedback() && (
          <div data-avoid className="mt-6 p-4 bg-white/5 border border-white/10 rounded-lg transition-all duration-300 animate-fade-in">
            <p className="text-white/90 text-sm leading-relaxed">{getFeedback()}</p>
          </div>
        )}

        <div data-avoid className="flex justify-between mt-8">
          {currentStep > 0 ? (
            <button
              onClick={handleBack}
              className="px-4 py-2 text-white/60 hover:text-white transition"
            >
              {t.quizBackButton}
            </button>
          ) : <div />}

          {selectedPoints !== null && (
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-white text-black rounded-lg hover:bg-opacity-90 transition font-medium"
            >
              {currentStep === 9 ? t.quizGetReportButton : t.quizNextButton}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderResult = () => {
    const category = getCategory();
    return (
      <div className="max-w-3xl mx-auto text-center">
        <div className="mb-8">
          <p className="text-sm text-white/50 uppercase tracking-wider mb-2">{t.quizScoreLabel}</p>
          <p data-quiz-focus data-avoid className="mx-auto inline-block text-6xl md:text-7xl font-bold mb-2" style={{ fontFamily: 'Open Sans' }}>
            {totalScore}<span className="text-2xl text-white/50">/30</span>
          </p>
          <p className="text-2xl font-semibold mb-4">{category.label}</p>
          <p className="text-white/80 max-w-xl mx-auto">{category.description}</p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/10 mb-8 text-left">
          {renderDimensionBars(false)}
        </div>

        {!emailSubmitted && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/10 mb-8">
            <p className="text-white/90 mb-6">{t.quizEmailCta}</p>
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.quizEmailPlaceholder}
                required
                className="flex-1 px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-white/50"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-white text-black rounded-lg hover:bg-opacity-90 transition font-medium whitespace-nowrap"
              >
                {t.quizGetReportButton}
              </button>
            </form>
            <p className="text-xs text-white/40 mt-3">{t.quizEmailNote}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleRetake}
            className="px-6 py-3 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition font-medium"
          >
            {t.quizRetakeButton}
          </button>
          <button
            onClick={handleShare}
            className="px-6 py-3 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition font-medium"
          >
            {copied ? t.quizCopied : t.quizShareButton}
          </button>
        </div>
      </div>
    );
  };

  const renderDetail = () => {
    const weakest = getWeakestDimension();
    const weakestDim = dimensions[weakest];
    const weakestDetail = dimensionDetail[weakest];

    return (
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm text-white/50 uppercase tracking-wider mb-2">{t.quizScoreLabel}</p>
          <p className="text-5xl font-bold mb-2" style={{ fontFamily: 'Open Sans' }}>
            {totalScore}<span className="text-xl text-white/50">/30</span>
          </p>
          <p className="text-xl font-semibold mb-4">{getCategory().label}</p>
          <p className="text-white/70 max-w-xl mx-auto">{getCategory().description}</p>
        </div>

        <div className="space-y-8">
          {Object.entries(dimensions).map(([key, dim]) => {
            const score = getDimensionScore(key);
            const pct = (score / dim.maxPoints) * 100;
            const isWeakest = key === weakest;
            const detail = dimensionDetail[key];
            const diagnosis = pct >= 70 ? detail.diagnosisHigh : pct >= 40 ? detail.diagnosisMid : detail.diagnosisLow;

            return (
              <div
                key={key}
                className={`bg-white/10 backdrop-blur-sm rounded-lg p-6 md:p-8 border-2 ${
                  isWeakest ? 'border-yellow-400/60' : 'border-white/10'
                }`}
              >
                {/* Header: label + score + bar */}
                <div className="flex justify-between items-center mb-1">
                  <h4 className="font-bold text-lg md:text-xl">
                    {dim.label}
                    {isWeakest && (
                      <span className="ml-2 text-xs bg-yellow-400/20 text-yellow-400 px-2 py-1 rounded font-semibold">{report.biggestOpportunity}</span>
                    )}
                  </h4>
                  <span className="text-white/60 font-semibold text-lg">{score}/{dim.maxPoints}</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3 mb-6">
                  <div
                    className="h-3 rounded-full transition-all duration-500"
                    style={{
                      width: animatedBars ? `${pct}%` : '0%',
                      backgroundColor: pct >= 70 ? '#4ade80' : pct >= 40 ? '#fbbf24' : '#f87171'
                    }}
                  />
                </div>

                {/* Personalized diagnosis */}
                <p className="text-white/90 leading-relaxed mb-6">{diagnosis}</p>

                {/* What the book teaches you */}
                <div className="bg-white/5 rounded-lg p-5 mb-5 border border-white/10">
                  <h5 className="font-semibold text-sm uppercase tracking-wider text-white/50 mb-3">{report.whatBookTeaches}</h5>
                  <p className="text-white/80 text-sm leading-relaxed">{detail.bookTeaser}</p>
                </div>

                {/* One thing you can do right now */}
                <div className="bg-white/5 rounded-lg p-5 mb-5 border border-white/10">
                  <h5 className="font-semibold text-sm uppercase tracking-wider text-white/50 mb-3">{report.doRightNow}</h5>
                  <p className="text-white/80 text-sm leading-relaxed">{detail.actionItem}</p>
                </div>

                {/* CTA to book */}
                <a
                  href={bookHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackAmazon(edition, 'kindle', 'amazon-us', 'quiz_detail');
                    trackQuizEvent('quiz_book_clicked', { weakest_dimension: key });
                  }}
                  className="inline-block px-5 py-2.5 bg-white text-black rounded-lg hover:bg-opacity-90 transition font-medium text-sm"
                >
                  {detail.chapter} &rarr; {t.quizGetBookButton}
                </a>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-10 bg-white/10 backdrop-blur-sm rounded-lg p-6 md:p-8 border border-white/20 text-center">
          <p className="text-lg text-white/90 mb-6">
            {report.weakestIntro} <strong>{weakestDim.label}</strong>. {weakestDetail.chapter} {report.startWith}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
            <a
              href={bookHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackAmazon(edition, 'kindle', 'amazon-us', 'quiz_summary');
              }}
              className="px-6 py-3 bg-white text-black rounded-lg hover:bg-opacity-90 transition font-medium text-center"
            >
              {t.quizGetBookButton}
            </a>
            <button
              onClick={handleShare}
              className="px-6 py-3 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition font-medium"
            >
              {copied ? t.quizCopied : t.quizShareButton}
            </button>
          </div>
          <p className="text-sm text-white/40">{report.availability}</p>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={handleRetake}
            className="px-6 py-3 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition font-medium"
          >
            {t.quizRetakeButton}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div id="quiz" ref={quizRef} className="min-h-screen flex flex-col justify-center py-16 md:py-24">
      <div className="container mx-auto px-4">
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fadeIn 0.3s ease-out forwards;
          }
        `}</style>
        <div className={`transition-opacity duration-300 ${fade ? 'opacity-100' : 'opacity-0'}`}>
          {currentStep === 'intro' && renderIntro()}
          {typeof currentStep === 'number' && renderQuestion()}
          {currentStep === 'result' && renderResult()}
          {currentStep === 'detail' && renderDetail()}
        </div>
      </div>
    </div>
  );
};

export default Quiz;
